import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Activity, AlertTriangle, Loader2, Thermometer, WifiOff } from 'lucide-react';
import { getWebSocketClient } from '../../utils/websocket-client';
import { computeVfdValues, findByRole, VFD_STATUS_COLORS } from '../../utils/vfd';
import { controllerApi, type ProcessController } from '../../utils/controllers';

interface ControllersPanelProps {
  clientId: string;
  token: string;
  initialControllers?: ProcessController[];
}

type Snapshot = { values: Record<string, number | null>; raws: Record<string, number>; errors: Record<string, string>; comm: string; timestamp: number };
type Runtime = { connected: boolean; comm?: string; error?: string | null; reconnecting?: boolean; at: number };
type CtlState = 'live' | 'alarm' | 'offline' | 'waiting';

const LED_PV = '#eef8ff'; // ice white digits on the graphite window

const STATE_META: Record<CtlState, { label: string; color: string; icon: any }> = {
  live: { label: 'Live', color: VFD_STATUS_COLORS.good, icon: Activity },
  alarm: { label: 'Alarm', color: VFD_STATUS_COLORS.critical, icon: AlertTriangle },
  offline: { label: 'No communication', color: VFD_STATUS_COLORS.warning, icon: WifiOff },
  waiting: { label: 'Waiting for data', color: VFD_STATUS_COLORS.neutral, icon: Loader2 },
};

const isNum = (v: any): v is number => typeof v === 'number' && Number.isFinite(v);
const fmt = (v: number | null | undefined, d = 1) => (isNum(v) ? v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—');

function useTween(target: number | null, duration = 600): number | null {
  const reduce = useReducedMotion();
  const [v, setV] = useState<number | null>(target);
  const from = useRef<number | null>(target);
  const raf = useRef(0);
  useEffect(() => {
    cancelAnimationFrame(raf.current);
    const f = from.current;
    if (!isNum(target) || !isNum(f) || reduce) { from.current = target; setV(target); return; }
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const val = f + (target - f) * (1 - Math.pow(1 - p, 3));
      from.current = val;
      setV(val);
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration, reduce]);
  return v;
}

function StatePill({ state, size = 'sm' }: { state: CtlState; size?: 'sm' | 'lg' }) {
  const m = STATE_META[state];
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs'}`}
      style={{ color: m.color, borderColor: `${m.color}55`, backgroundColor: `${m.color}14` }}>
      {state === 'alarm' || state === 'live' ? (
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:hidden" style={{ backgroundColor: m.color }} />
          <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: m.color }} />
        </span>
      ) : <Icon className={`${size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5'} ${state === 'waiting' ? 'animate-spin' : ''}`} />}
      {m.label}
    </span>
  );
}

/** Selec-style 48x48 faceplate: ice white PV and its unit on a graphite window in a titanium frame. Same in both themes. */
function Faceplate({ pv, online, decimals, unit }: { pv: number | null; online: boolean; decimals: number; unit: string }) {
  const pvT = useTween(online ? pv : null);
  return (
    <div className="rounded-xl bg-gradient-to-br from-[#e4e7eb] via-[#b9bfc7] to-[#878e98] p-2 shadow-[0_4px_14px_rgba(40,48,60,0.3),inset_0_1px_0_rgba(255,255,255,0.7)] ring-1 ring-[#6b727c]/60">
      <div className="rounded-lg bg-gradient-to-b from-[#1b2129] to-[#252c36] px-3 py-2 ring-1 ring-black/40 shadow-[inset_0_2px_8px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-end gap-2">
          <span className="font-mono text-4xl font-bold tabular-nums tracking-wider" style={{ color: LED_PV, textShadow: online ? `0 0 10px #9fd8ffaa` : 'none', opacity: online ? 1 : 0.35 }}>
            {online && isNum(pvT) ? fmt(pvT, decimals) : '----'}
          </span>
          <div className="flex min-w-8 shrink-0 flex-col gap-1 leading-none">
            <span className="text-[10px] font-bold text-[#9fb4c8]">PV</span>
            <span className="text-sm font-bold text-[#eef8ff]">{unit || '°C'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ControllersPanel({ clientId, token, initialControllers }: ControllersPanelProps) {
  const [list, setList] = useState<ProcessController[]>(initialControllers || []);
  const [loading, setLoading] = useState(!initialControllers);
  const [live, setLive] = useState<Record<string, Snapshot>>({});
  const [status, setStatus] = useState<Record<string, Runtime>>({});
  const [, setTick] = useState(0);

  useEffect(() => {
    if (initialControllers) return;
    let cancelled = false;
    controllerApi.listForClient(token, clientId)
      .then((l) => { if (!cancelled) setList(l); })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [clientId, token, initialControllers]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const ws = getWebSocketClient(token, { clientId });
    const onLive = (data: any) => {
      const id = data?.deviceId || data?.vfdId;
      if (!id) return;
      // Receipt time, not the bridge PC clock: a bridge clock a few seconds off made
      // every packet look old ("4s ago") or even stale.
      const ts = Date.now();
      setLive((p) => ({ ...p, [id]: { values: data.values || {}, raws: data.raws || {}, errors: data.errors || {}, comm: data.comm || 'ok', timestamp: ts } }));
    };
    const onStatus = (data: any) => {
      const id = data?.deviceId || data?.vfdId;
      if (!id) return;
      setStatus((p) => {
        const cur = p[id];
        const nx: Runtime = { connected: !!data.connected, comm: data.comm, error: data.error, reconnecting: !!data.reconnecting, at: Date.now() };
        if (cur && cur.connected === nx.connected && cur.comm === nx.comm && cur.error === nx.error && Date.now() - cur.at < 4000) return p;
        return { ...p, [id]: nx };
      });
    };
    ws.on('controller_live_data', onLive);
    ws.on('controller_status_update', onStatus);
    ws.connect();
    return () => { ws.off('controller_live_data', onLive); ws.off('controller_status_update', onStatus); };
  }, [clientId, token]);

  const views = list.map((c) => {
    const snap = live[c.id];
    const st = status[c.id];
    const computed = computeVfdValues(c.items, snap?.values, snap?.errors);
    const fresh = !!snap && Date.now() - snap.timestamp < Math.max(15000, (c.pollIntervalMs || 1000) * 6);
    const online = fresh && snap.comm !== 'error' && st?.connected !== false;
    const get = (role: string) => findByRole(c.items, computed, role);
    const pv = online ? get('pv')?.num ?? null : null;
    const al1 = !!get('alarm1')?.flag;
    const al2 = !!get('alarm2')?.flag;
    const fault = !!get('fault')?.flag;
    let state: CtlState;
    if (!online) state = snap || st ? 'offline' : 'waiting';
    else if (al1 || al2 || fault) state = 'alarm';
    else state = 'live';
    return { c, computed, online, pv, state };
  });

  if (loading) return <div className="flex h-72 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading controllers…</div>;
  if (list.length === 0) {
    return (
      <Card className="p-12 text-center">
        <Thermometer className="mx-auto mb-4 h-14 w-14 text-muted-foreground opacity-50" />
        <h3 className="mb-2 text-xl font-semibold">No controllers configured</h3>
        <p className="text-muted-foreground">Controllers are added by your administrator in the Admin Panel → Controller Connection.</p>
      </Card>
    );
  }

  const count = (s: CtlState | CtlState[]) => views.filter((v) => (Array.isArray(s) ? s.includes(v.state) : v.state === s)).length;
  const summary = [
    { label: 'Controllers', value: list.length, icon: Thermometer, color: undefined as string | undefined },
    { label: 'Live', value: count('live'), icon: Activity, color: VFD_STATUS_COLORS.good },
    { label: 'Alarm', value: count('alarm'), icon: AlertTriangle, color: count('alarm') > 0 ? VFD_STATUS_COLORS.critical : undefined },
    { label: 'No comms', value: count(['offline', 'waiting']), icon: WifiOff, color: count('offline') > 0 ? VFD_STATUS_COLORS.warning : undefined },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {summary.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className={`relative overflow-hidden border-border/60 ${s.label === 'Alarm' && s.value > 0 ? 'ring-1 ring-[#d03b3b]/50' : ''}`}>
              {s.label === 'Alarm' && s.value > 0 && <div className="pointer-events-none absolute inset-0 animate-pulse bg-[#d03b3b]/10 motion-reduce:animate-none" />}
              <CardContent className="relative flex items-center gap-3 p-4">
                <div className="rounded-lg bg-muted p-2" style={s.color ? { color: s.color, backgroundColor: `${s.color}1a` } : undefined}><s.icon className="h-5 w-5" /></div>
                <div><div className="text-xs text-muted-foreground">{s.label}</div><div className="text-xl font-bold tabular-nums">{s.value}</div></div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Faceplate wall */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {views.map((v, i) => {
          const pvItem = findByRole(v.c.items, v.computed, 'pv')?.item;
          return (
            <motion.div key={v.c.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + i * 0.05 }}
              className="relative overflow-hidden rounded-2xl border border-border/60 bg-card p-3 text-left">
              <div className="relative mb-2 flex items-start justify-between gap-2 px-1">
                <div className="min-w-0">
                  <div className="truncate font-semibold">{v.c.name}</div>
                  {v.c.location && <div className="truncate text-xs text-muted-foreground">{v.c.location}</div>}
                </div>
                <StatePill state={v.state} />
              </div>
              <div className="relative">
                <Faceplate pv={v.pv} online={v.online} decimals={pvItem?.decimals ?? 1} unit={pvItem?.unit || '°C'} />
              </div>
            </motion.div>
          );
        })}
      </div>

    </div>
  );
}
